import { Button, Icon, Input, cn } from "@nexus/ui";
import { useLayoutEffect, useRef, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  orderLineVideoDisplayName,
  orderLineVideoLabelEditable,
} from "@/features/marketplace/lib/order-line-videos";
import type { OrderLineVideo } from "@/features/marketplace/types/marketplace.types";

const LABEL_INPUT_CLASS =
  "border-primary/40 focus-visible:border-primary focus-visible:ring-primary/20 h-7 min-w-0 flex-1 rounded-sm bg-background px-2 text-xs shadow-none focus-visible:ring-1";

interface OrderLineVideoLabelEditorProps {
  video: OrderLineVideo;
  editable?: boolean;
  isPending?: boolean;
  onSave: (label: string | null) => void;
  className?: string;
}

export function OrderLineVideoLabelEditor({
  video,
  editable = orderLineVideoLabelEditable(video),
  isPending = false,
  onSave,
  className,
}: OrderLineVideoLabelEditorProps) {
  const { t } = useTranslation();
  const displayName = orderLineVideoDisplayName(video, t);
  const inputRef = useRef<HTMLInputElement>(null);
  const ignoreBlurCommitRef = useRef(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(displayName);

  useLayoutEffect(() => {
    if (!isEditing) {
      setDraft(displayName);
    }
  }, [displayName, isEditing]);

  useLayoutEffect(() => {
    if (!isEditing) return;
    ignoreBlurCommitRef.current = true;
    const input = inputRef.current;
    if (input) {
      input.focus();
      input.select();
    }
    const frame = window.requestAnimationFrame(() => {
      ignoreBlurCommitRef.current = false;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isEditing]);

  const cancelEdit = () => {
    setDraft(displayName);
    setIsEditing(false);
  };

  const commitEdit = () => {
    const trimmed = draft.trim();
    const nextLabel =
      trimmed.length === 0 || trimmed === orderLineVideoDisplayName(
        { position: video.position, label: null },
        t,
      )
        ? null
        : trimmed;
    const currentCustom = video.label?.trim() ?? null;
    if (nextLabel === currentCustom) {
      cancelEdit();
      return;
    }
    onSave(nextLabel);
    setIsEditing(false);
  };

  const handleBlur = () => {
    if (ignoreBlurCommitRef.current) return;
    commitEdit();
  };

  const startEditing = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (!editable) return;
    setDraft(video.label?.trim() ?? displayName);
    setIsEditing(true);
  };

  if (isEditing) {
    return (
      <div
        className={cn("flex min-w-0 items-center gap-1", className)}
        onClick={(event) => event.stopPropagation()}
      >
        <Input
          ref={inputRef}
          value={draft}
          disabled={isPending}
          aria-label={t(
            "marketplace.partner.videos.renameAria",
            "Video name",
          )}
          className={LABEL_INPUT_CLASS}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitEdit();
            } else if (event.key === "Escape") {
              event.preventDefault();
              cancelEdit();
            }
          }}
          onBlur={handleBlur}
        />
      </div>
    );
  }

  return (
    <div
      className={cn("group/label flex min-w-0 items-center gap-1", className)}
    >
      <p className="min-w-0 truncate text-xs font-medium text-foreground">
        {displayName}
      </p>
      {editable ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-6 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/label:opacity-100 group-focus-within/label:opacity-100 motion-reduce:transition-none [@media(hover:none)]:opacity-100"
          aria-label={t(
            "marketplace.partner.videos.renameAria",
            "Rename video",
          )}
          disabled={isPending}
          onClick={startEditing}
        >
          <Icon name="pencil" size="xs" color="inherit" aria-hidden />
        </Button>
      ) : null}
    </div>
  );
}
